/**
 * The register's Filters disclosure (SA-462, spec §8.2, D13): CURRENT STEP, STATUS,
 * ORDER STATUS (with a status map, SA-464) and QUEUE rows of toggle chips over the same `useOrders` setters the
 * retired selects called, and Clear all. Mounted only while open, only on
 * the register lane — it calls `useStatusMap` (React Query), exactly as
 * `RegisterFilters` did.
 * SA-468: hairlines between rows behind a 92px label gutter, the step row's
 * seats in a first column with every first chip on one line (SA-1184), and
 * every chip wearing its cell's tone as a dot — the executor tone on a step,
 * the config's dot on a queue state or an order status, the pack's tone on a
 * status.
 */
import { Fragment, type ReactNode } from 'react'
import { cn } from '@/utils'
import { useStatusMap } from '@/hooks/useRecordExtras'
import { STATUS_CONFIG, COMMERCE_LADDER_STATUSES } from '@/components/orders/orderStatusConfig'
import { QUEUE_STATE_CONFIG } from '@/constants/queueStates'
import type { OrderStatus } from '@/types/commerce'
import { executorTone } from '@/components/process/executorTone'
import { TONE_DOT } from './tones'
import {
  groupStepsByLane, type RegisterFilterSetters, type RegisterFilterState, type StepOption,
} from './registerFilterModel'

export interface RegisterFilterPanelProps {
  /** The `aria-controls` target of the Filters button. */
  id: string
  steps: StepOption[]
  filters: RegisterFilterState
  setters: RegisterFilterSetters
  /** `stats.queue.byState`; null until the stats read answers. */
  queueCounts: Record<string, number> | null
  showCommerce: boolean
  onClearAll: () => void
}

const CHIP = 'inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors'
// Pressed is the foreground fill the process list uses for the current step
// (SA-468): primary sat beside the info dots as two adjacent blues.
const ON = 'border-foreground bg-foreground text-background'
const OFF = 'border-border bg-background text-foreground hover:bg-muted'

function Chip({ pressed, onClick, dot, children }: {
  pressed: boolean; onClick: () => void; dot?: string | null; children: ReactNode
}) {
  return (
    <button type="button" aria-pressed={pressed} onClick={onClick} className={cn(CHIP, pressed ? ON : OFF)}>
      {/* A dot, never a wash: washes are facts (the table), dots are choices (R1). */}
      {dot && (
        <span
          aria-hidden="true"
          className={cn('h-[7px] w-[7px] shrink-0 rounded-full', dot, pressed && 'ring-1 ring-background')}
        />
      )}
      {children}
    </button>
  )
}

function Row({ label, children, trailing }: { label: string; children: ReactNode; trailing?: ReactNode }) {
  return (
    <div className="flex items-start gap-2 py-2.5 first:pt-0 last:pb-0">
      <span className="min-w-[92px] shrink-0 pt-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">{children}</div>
      {trailing}
    </div>
  )
}

function stepDot(step: StepOption): string | null {
  return step.executorType ? executorTone({ executorType: step.executorType, level: step.level ?? null }).dot : null
}

export function RegisterFilterPanel({
  id, steps, filters, setters, queueCounts, showCommerce, onClearAll,
}: RegisterFilterPanelProps) {
  const statusMap = useStatusMap()
  const statuses = statusMap.data?.statuses ?? []
  // A chip pressed again clears its filter: one control, both directions.
  const toggle = (current: string, next: string, set: (v: string) => void) => set(current === next ? '' : next)
  // The order ladder (`status=`): the STATUS row without a map, its own
  // ORDER STATUS row under the map's statuses with one (SA-464, owner
  // ruling 2026-09-23: the old lane offered both selects, so the register
  // lane offers both rows). The commerce rule is the same on either.
  const ladder = Object.entries(STATUS_CONFIG)
    .filter(([key]) => showCommerce || !COMMERCE_LADDER_STATUSES.has(key as OrderStatus))
    .map(([key, config]) => (
      <Chip key={key} pressed={filters.orderStatus === key} dot={config.dot}
        onClick={() => toggle(filters.orderStatus, key, setters.orderStatus)}>
        {config.label}
      </Chip>
    ))

  const groups = groupStepsByLane(steps)
  const stepChip = (step: StepOption) => (
    <Chip key={step.id} pressed={filters.step === step.id} dot={stepDot(step)}
      onClick={() => toggle(filters.step, step.id, setters.step)}>
      {step.number} {step.title}
    </Chip>
  )

  return (
    <div id={id} role="region" aria-label="Filters" className="divide-y divide-border rounded-lg border border-border bg-card p-3">
      {/* SA-1184: the register's Current step column is what this filters. Every
          seat's first chip starts on one line: seats in a first column, each
          group's chips wrapping in the second, from a 28rem panel (a container
          query); narrower, a seat's word sits above its chips. No seat column
          when no step has a seat. */}
      <Row label="Current step">
        {groups.some((group) => group.lane) ? (
          <div className="@container w-full">
            <div data-testid="step-groups"
              className="grid grid-cols-1 gap-y-1.5 @md:grid-cols-[max-content_minmax(0,1fr)] @md:gap-x-3">
              {groups.map((group) => (
                <Fragment key={group.lane ?? ''}>
                  <span aria-hidden={group.lane ? undefined : true}
                    className={cn('pt-1 text-[11px] font-medium uppercase tracking-wide text-foreground/70',
                      !group.lane && 'hidden @md:block')}>
                    {group.lane ?? ''}
                  </span>
                  <div className="flex min-w-0 flex-wrap items-center gap-1.5">{group.steps.map(stepChip)}</div>
                </Fragment>
              ))}
            </div>
          </div>
        ) : groups.flatMap((group) => group.steps).map(stepChip)}
      </Row>
      <Row label="Status">
        {statuses.length > 0
          ? statuses.map((status) => (
            <Chip key={status.key} pressed={filters.commercialStatus === status.key} dot={TONE_DOT[status.tone ?? 'info']}
              onClick={() => toggle(filters.commercialStatus, status.key, setters.commercialStatus)}>
              {status.label}
            </Chip>
          ))
          : ladder}
      </Row>
      {statuses.length > 0 && <Row label="Order status">{ladder}</Row>}
      {/* SA-717: records closed as not an enquiry are left out unless asked for. */}
      <Row label="Rejected">
        <Chip pressed={filters.includeRejected} onClick={() => setters.includeRejected(!filters.includeRejected)}>
          Show rejected
        </Chip>
      </Row>
      <Row label="Queue" trailing={
        <button type="button" onClick={onClearAll}
          className="ml-auto shrink-0 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground">
          Clear all
        </button>
      }>
        {Object.entries(QUEUE_STATE_CONFIG).map(([state, config]) => (
          <Chip key={state} pressed={filters.queueState === state} dot={config.dot}
            onClick={() => toggle(filters.queueState, state, setters.queueState)}>
            {config.label}
            {queueCounts && <>{' '}<span className="tabular-nums opacity-80">{queueCounts[state] ?? 0}</span></>}
          </Chip>
        ))}
      </Row>
    </div>
  )
}
